import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '../../lib/utils';

// Lightweight listbox-style select. Items are plain strings (value === label),
// which is all the marketing pages need. Supports controlled (value) and
// uncontrolled (defaultValue) use plus a hidden input for native form posts.
const SelectContext = React.createContext(null);

function Select({ value, defaultValue, onValueChange, name, className, children }) {
	const [inner, setInner] = React.useState(defaultValue);
	const [open, setOpen] = React.useState(false);
	const rootRef = React.useRef(null);
	const current = value !== undefined ? value : inner;

	React.useEffect(() => {
		if (!open) return undefined;
		const onDown = (event) => {
			if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
		};
		const onKey = (event) => {
			if (event.key === 'Escape') setOpen(false);
		};
		document.addEventListener('mousedown', onDown);
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('mousedown', onDown);
			document.removeEventListener('keydown', onKey);
		};
	}, [open]);

	const select = (next) => {
		if (value === undefined) setInner(next);
		if (onValueChange) onValueChange(next);
		setOpen(false);
	};

	return (
		<SelectContext.Provider value={{ current, open, setOpen, select }}>
			<div ref={rootRef} className={cn('relative', className)}>
				{name ? <input type="hidden" name={name} value={current ?? ''} /> : null}
				{children}
			</div>
		</SelectContext.Provider>
	);
}

function SelectValue({ placeholder }) {
	const { current } = React.useContext(SelectContext);
	return (
		<span className={cn(!current && 'text-muted-foreground')}>
			{current || placeholder}
		</span>
	);
}

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => {
	const { open, setOpen } = React.useContext(SelectContext);
	return (
		<button
			type="button"
			ref={ref}
			aria-haspopup="listbox"
			aria-expanded={open}
			onClick={() => setOpen(!open)}
			className={cn(
				'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
				className
			)}
			{...props}
		>
			{children}
			<ChevronDown className="h-4 w-4 opacity-50" />
		</button>
	);
});
SelectTrigger.displayName = 'SelectTrigger';

function SelectContent({ className, align = 'start', sideOffset = 4, children }) {
	const { open } = React.useContext(SelectContext);
	if (!open) return null;
	return (
		<div
			role="listbox"
			style={{ marginTop: sideOffset }}
			className={cn(
				'absolute top-full z-50 max-h-72 min-w-full overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md',
				align === 'full' ? 'inset-x-0' : align === 'end' ? 'right-0' : 'left-0',
				className
			)}
		>
			{children}
		</div>
	);
}

function SelectItem({ className, value, children }) {
	const { current, select } = React.useContext(SelectContext);
	const selected = current === value;
	return (
		<div
			role="option"
			aria-selected={selected}
			tabIndex={0}
			onClick={() => select(value)}
			onKeyDown={(event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					select(value);
				}
			}}
			className={cn(
				'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
				className
			)}
		>
			<span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
				{selected ? <Check className="h-4 w-4" /> : null}
			</span>
			{children}
		</div>
	);
}

export { Select, SelectValue, SelectTrigger, SelectContent, SelectItem };
